import { Link } from "@tanstack/react-router";
import { ArrowLeft, Clock, Calendar, ChevronRight, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MarkdownLite } from "./MarkdownLite";
import { JsonLd } from "./JsonLd";
import { articleSchema, breadcrumbSchema, faqPageSchema } from "@/lib/schemas";
import type { BlogPost } from "@/lib/blog-data";
import { getRelatedPosts } from "@/lib/blog-data";

interface BlogLayoutProps {
  post: BlogPost;
}

export function BlogLayout({ post }: BlogLayoutProps) {
  const related = getRelatedPosts(post.slug);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const article = document.getElementById("article-body");
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = -rect.top;
      const pct = Math.min(100, Math.max(0, (scrolled / total) * 100));
      setProgress(pct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active TOC section
  useEffect(() => {
    const headings = post.toc
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [post.toc]);

  const breadcrumbs = [
    { name: "Strona główna", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <JsonLd
        data={articleSchema({
          title: post.title,
          description: post.metaDescription,
          path: `/blog/${post.slug}`,
          datePublished: post.publishedAt,
          keywords: post.keywords,
        })}
      />
      <JsonLd data={breadcrumbSchema(breadcrumbs)} />
      {post.faqs && post.faqs.length > 0 && (
        <JsonLd data={faqPageSchema(post.faqs.map((f) => ({ q: f.q, a: f.a })))} />
      )}

      <Header />

      {/* Reading progress bar */}
      <div className="fixed left-0 right-0 top-0 z-40 h-1 bg-transparent">
        <div
          className="h-full bg-gradient-to-r from-primary to-primary-glow transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <article className="mx-auto max-w-7xl px-6 pb-24 pt-32">
        {/* Breadcrumbs */}
        <nav className="mb-8 flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="truncate text-foreground">{post.category}</span>
        </nav>

        <div className="grid gap-12 lg:grid-cols-[1fr_280px]">
          {/* Article body */}
          <div id="article-body">
            <header className="mb-10">
              <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                {post.category}
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold md:text-5xl">
                {post.title}
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">{post.excerpt}</p>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {new Date(post.publishedAt).toLocaleDateString("pl-PL", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {post.readingTime} min czytania
                </span>
                <span>SyncTech Team</span>
              </div>
            </header>

            {/* Inline lead magnet (in middle, after intro - shown after first H2) */}
            <MarkdownLite source={post.content} />

            {/* FAQ */}
            {post.faqs && post.faqs.length > 0 && (
              <section id="faq" className="mt-16">
                <h2 className="scroll-mt-28 font-display text-2xl font-bold md:text-3xl">
                  Najczęściej zadawane pytania
                </h2>
                <div className="mt-6 space-y-3">
                  {post.faqs.map((f, i) => (
                    <details
                      key={i}
                      className="group rounded-2xl border border-border bg-card/40 px-5 py-4 transition hover:border-primary/50"
                    >
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-display text-base font-semibold text-foreground">
                        <span>{f.q}</span>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-primary transition group-open:rotate-90" />
                      </summary>
                      <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* Bottom CTA */}
            <div className="mt-16 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-primary-glow/10 p-8">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-4 w-4" />
                Potrzebujesz wsparcia?
              </div>
              <h3 className="mt-3 font-display text-2xl font-bold md:text-3xl">
                Bezpłatna konsultacja — 30 minut, konkretne odpowiedzi
              </h3>
              <p className="mt-3 text-muted-foreground">
                Powiedz mi, gdzie jesteś i dokąd chcesz dojść. Wyjdziemy z planem działania, niezależnie czy zdecydujesz się na współpracę.
              </p>
              <a
                href="/#kontakt"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-primary-glow px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition hover:scale-[1.03]"
              >
                Umów konsultację
                <ChevronRight className="h-4 w-4" />
              </a>
            </div>

            {/* Related posts */}
            {related.length > 0 && (
              <section className="mt-16">
                <h3 className="mb-6 font-display text-2xl font-bold">Powiązane artykuły</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {related.map((r) => (
                    <a
                      key={r.slug}
                      href={`/blog/${r.slug}`}
                      className="group block rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-1 hover:border-primary hover:shadow-elegant"
                    >
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        {r.category}
                      </span>
                      <h4 className="mt-2 font-display text-base font-semibold leading-tight group-hover:text-primary">
                        {r.title}
                      </h4>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {r.readingTime} min czytania
                      </p>
                    </a>
                  ))}
                </div>
              </section>
            )}

            <Link
              to="/blog"
              className="mt-12 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Wszystkie artykuły
            </Link>
          </div>

          {/* Sticky sidebar with TOC + lead magnet */}
          <aside className="relative">
            <div className="sticky top-28 space-y-6">
              {/* TOC */}
              <nav className="rounded-2xl border border-border bg-card/40 p-5">
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Spis treści
                </p>
                <ul className="space-y-2 text-sm">
                  {post.toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        className={`block border-l-2 pl-3 transition ${
                          activeSection === t.id
                            ? "border-primary text-foreground"
                            : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        }`}
                      >
                        {t.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Sidebar lead magnet */}
              <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary-glow/5 p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Bezpłatny PDF
                </p>
                <h4 className="mt-2 font-display text-base font-bold leading-tight">
                  Checklist 25 integracji e-commerce
                </h4>
                <p className="mt-2 text-xs text-muted-foreground">
                  Pobierz listę kontrolną wdrożenia BaseLinker + sklep + marketplace.
                </p>
                <a
                  href="/#lead-magnet"
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
                >
                  Pobierz PDF
                </a>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
}
